/**
 * Data Integrity Dashboard
 *
 * JEFF DEAN PRINCIPLE: Real-time visibility into data health at scale
 *
 * This dashboard provides:
 * - Overview of all companies' data integrity status
 * - Anomaly detection results
 * - Historical integrity reports
 * - Quick actions for investigation
 *
 * @since 2025-12-16
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { formatDistanceToNow } from 'date-fns';
import { Play, CheckCircle2, AlertTriangle, AlertCircle, Building2, Clock, ExternalLink } from 'lucide-react';

interface DataIntegrityReport {
  reportId: string;
  reportDate: { _seconds: number };
  generatedBy: 'scheduled' | 'manual';
  executionTimeMs: number;
  summary: {
    companiesChecked: number;
    companiesWithAnomalies: number;
    totalAnomalies: number;
    criticalAnomalies: number;
    warningAnomalies: number;
  };
  status: 'healthy' | 'warnings' | 'critical';
}

interface DetectedAnomaly {
  type: string;
  severity: 'info' | 'warning' | 'critical';
  companyId: string;
  periodMonth?: string;
  message: string;
  expected?: number;
  actual?: number;
  variancePct?: number;
}

interface CompanyAnomalyData {
  companyName?: string;
  anomalyCount: number;
  criticalCount: number;
  anomalies: DetectedAnomaly[];
}

export const DataIntegrityDashboard: React.FC = () => {
  const [latestReport, setLatestReport] = useState<DataIntegrityReport | null>(null);
  const [anomaliesByCompany, setAnomaliesByCompany] = useState<Record<string, CompanyAnomalyData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRunningManualCheck, setIsRunningManualCheck] = useState(false);

  useEffect(() => {
    fetchLatestReport();
  }, []);

  const fetchLatestReport = async () => {
    try {
      setLoading(true);
      const getReport = httpsCallable(functions, 'getLatestIntegrityReport');
      const result = await getReport({});
      const data = result.data as { report: DataIntegrityReport; anomaliesByCompany: Record<string, CompanyAnomalyData> } | null;

      if (data) {
        setLatestReport(data.report);
        setAnomaliesByCompany(data.anomaliesByCompany || {});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const runManualCheck = async () => {
    try {
      setIsRunningManualCheck(true);
      const runCheck = httpsCallable(functions, 'runManualIntegrityCheck');
      await runCheck({});
      await fetchLatestReport();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run check');
    } finally {
      setIsRunningManualCheck(false);
    }
  };

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'healthy':
        return {
          bg: 'bg-emerald-500/10',
          border: 'border-emerald-500/30',
          text: 'text-emerald-400',
          icon: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
        };
      case 'warnings':
        return {
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/30',
          text: 'text-amber-400',
          icon: <AlertTriangle className="w-5 h-5 text-amber-400" />,
        };
      case 'critical':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/30',
          text: 'text-red-400',
          icon: <AlertCircle className="w-5 h-5 text-red-400" />,
        };
      default:
        return {
          bg: 'bg-slate-500/10',
          border: 'border-slate-500/30',
          text: 'text-slate-400',
          icon: null,
        };
    }
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500/10 border-red-500/30 text-red-300';
      case 'warning':
        return 'bg-amber-500/10 border-amber-500/30 text-amber-300';
      default:
        return 'bg-slate-700/50 border-slate-600/50 text-slate-300';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />;
      default:
        return <CheckCircle2 className="w-4 h-4 text-slate-400 flex-shrink-0" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Action Button */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-slate-400">
            Monitor commission data health across all companies
          </p>
        </div>
        <button
          onClick={runManualCheck}
          disabled={isRunningManualCheck}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-500 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg shadow-violet-500/25"
        >
          <Play className={`w-4 h-4 ${isRunningManualCheck ? 'animate-pulse' : ''}`} />
          {isRunningManualCheck ? 'Running Check...' : 'Run Manual Check'}
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-300">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 text-sm underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {latestReport && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Overall Status */}
          {(() => {
            const statusStyles = getStatusStyles(latestReport.status);
            return (
              <div className={`rounded-xl border p-5 ${statusStyles.bg} ${statusStyles.border} backdrop-blur-sm`}>
                <div className="flex items-center gap-2 mb-2">
                  {statusStyles.icon}
                  <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wide">Overall Status</h3>
                </div>
                <p className={`text-2xl font-bold capitalize ${statusStyles.text}`}>{latestReport.status}</p>
                <p className="text-sm text-slate-500 mt-1">
                  Last check: {formatDistanceToNow(new Date(latestReport.reportDate._seconds * 1000), { addSuffix: true })}
                </p>
              </div>
            );
          })()}

          {/* Companies Checked */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wide">Companies Checked</h3>
            </div>
            <p className="text-2xl font-bold text-white">{latestReport.summary.companiesChecked}</p>
            <p className="text-sm text-slate-500 mt-1">
              <span className={latestReport.summary.companiesWithAnomalies > 0 ? 'text-amber-400' : 'text-slate-400'}>
                {latestReport.summary.companiesWithAnomalies}
              </span> with issues
            </p>
          </div>

          {/* Critical Anomalies */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className={`w-5 h-5 ${latestReport.summary.criticalAnomalies > 0 ? 'text-red-400' : 'text-slate-400'}`} />
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wide">Critical Issues</h3>
            </div>
            <p className={`text-2xl font-bold ${latestReport.summary.criticalAnomalies > 0 ? 'text-red-400' : 'text-white'}`}>
              {latestReport.summary.criticalAnomalies}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              <span className={latestReport.summary.warningAnomalies > 0 ? 'text-amber-400' : 'text-slate-400'}>
                {latestReport.summary.warningAnomalies}
              </span> warnings
            </p>
          </div>

          {/* Execution Time */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-purple-400" />
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wide">Check Duration</h3>
            </div>
            <p className="text-2xl font-bold text-white">
              {(latestReport.executionTimeMs / 1000).toFixed(1)}s
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Generated: <span className="text-slate-400">{latestReport.generatedBy}</span>
            </p>
          </div>
        </div>
      )}

      {/* Anomalies List */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 backdrop-blur-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-700/50">
          <h2 className="text-lg font-semibold text-white">Detected Anomalies</h2>
        </div>

        {Object.keys(anomaliesByCompany).length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-slate-300 font-medium">No anomalies detected</p>
            <p className="text-slate-500 text-sm mt-1">All companies are healthy!</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {Object.entries(anomaliesByCompany).map(([companyId, data]) => (
              <div key={companyId} className="p-5 hover:bg-slate-700/30 transition-colors">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-white text-lg">
                      {data.companyName || companyId}
                    </h3>
                    <p className="text-sm text-slate-400 mt-0.5">
                      {data.anomalyCount} anomalies
                      {data.criticalCount > 0 && (
                        <span className="text-red-400 font-medium"> ({data.criticalCount} critical)</span>
                      )}
                    </p>
                  </div>
                  <a
                    href={`/companies/${companyId}`}
                    className="flex items-center gap-1.5 text-violet-400 text-sm hover:text-violet-300 transition-colors"
                  >
                    View Company
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>

                <div className="space-y-2">
                  {data.anomalies.slice(0, 5).map((anomaly, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start gap-3 text-sm p-3 rounded-lg border ${getSeverityStyles(anomaly.severity)}`}
                    >
                      {getSeverityIcon(anomaly.severity)}
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{anomaly.type}</span>
                        {anomaly.periodMonth && (
                          <span className="ml-2 text-slate-500">({anomaly.periodMonth})</span>
                        )}
                        <p className="text-slate-400 mt-0.5">{anomaly.message}</p>
                      </div>
                    </div>
                  ))}
                  {data.anomalies.length > 5 && (
                    <p className="text-sm text-slate-500 pl-3">
                      + {data.anomalies.length - 5} more anomalies
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* No Report State */}
      {!latestReport && !loading && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-12 text-center backdrop-blur-sm">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-500/10 mb-4">
            <AlertTriangle className="w-8 h-8 text-violet-400" />
          </div>
          <p className="text-slate-300 font-medium mb-2">No integrity reports found</p>
          <p className="text-slate-500 text-sm mb-6">Run a manual check to generate your first report.</p>
          <button
            onClick={runManualCheck}
            disabled={isRunningManualCheck}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-500 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg shadow-violet-500/25"
          >
            <Play className={`w-4 h-4 ${isRunningManualCheck ? 'animate-pulse' : ''}`} />
            {isRunningManualCheck ? 'Running...' : 'Run First Check'}
          </button>
        </div>
      )}
    </div>
  );
};

export default DataIntegrityDashboard;
